import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import withContainer from './withContainer'

const withPopover = (WrappedComponent) => {
    return ({
        placement = 'top',
        ContentComponent,
        contentProps = {},
        popoverOffsetX = 0,
        popoverOffsetY = 0,
        trigger = 'click',
        borderColor,
        defaultOpen = false,
        closeOnOutsideClick = true,
        onChange,
        ...props
    }) => {
        const [isOpen, setIsOpen] = useState(defaultOpen)
        const triggerRef = useRef(null)
        const popoverNodeRef = useRef(null)

        useEffect(() => {
            setIsOpen(defaultOpen)
        }, [defaultOpen])

        useEffect(() => {
            if (!isOpen || !closeOnOutsideClick) return
            const onDocMouseDown = (e) => {
                const t = e.target
                if (triggerRef.current?.contains(t)) return
                if (popoverNodeRef.current?.contains(t)) return
                hidePopover()
            }
            document.addEventListener('mousedown', onDocMouseDown)
            return () => document.removeEventListener('mousedown', onDocMouseDown)
        }, [isOpen, closeOnOutsideClick])
        useEffect(() => {
            onChange?.(isOpen)
            if (!isOpen) return
            const handleResize = () => {
                hidePopover()
            }
            window.addEventListener('resize', handleResize)
            return () => window.removeEventListener('resize', handleResize)
        }, [isOpen])

        const showPopover = () => {
            setIsOpen(true)
        }
        const hidePopover = () => {
            setIsOpen(false)
        }
        const togglePopover = () => setIsOpen((prev) => !prev)

        const eventHandlers =
            trigger === 'click' ? { onClick: togglePopover } : { onMouseEnter: showPopover, onMouseLeave: hidePopover }

        const renderContent = (injected) => {
            if (!ContentComponent) return null
            if (React.isValidElement(ContentComponent)) {
                return React.cloneElement(ContentComponent, injected)
            }
            const Comp = ContentComponent
            return <Comp {...injected} />
        }

        return (
            <>
                <div
                    ref={triggerRef}
                    {...eventHandlers}
                    style={{ display: 'inline-block', cursor: 'pointer' }}
                    className={'popover-container'}
                >
                    <WrappedComponent
                        {...props}
                        isOpen={isOpen}
                        children={typeof props.children === 'function' ? props.children({ isOpen }) : props.children}
                    />
                </div>

                {isOpen &&
                    createPortal(
                        <PopoverContainer
                            targetRef={triggerRef}
                            placement={placement}
                            popoverOffsetX={popoverOffsetX}
                            popoverOffsetY={popoverOffsetY}
                            borderColor={borderColor}
                            className={'p-0'}
                            onMount={(node) => {
                                popoverNodeRef.current = node
                            }}
                        >
                            {/*{ContentComponent ? <ContentComponent {...contentProps} /> : null}*/}
                            {renderContent({ togglePopover })}
                        </PopoverContainer>,
                        document.body
                    )}
            </>
        )
    }
}

const PopoverContainer = withContainer(
    ({ targetRef, placement, popoverOffsetX, popoverOffsetY, borderColor, children, onMount }) => {
        const popoverRef = useRef(null)
        const [style, setStyle] = useState({})
        const [arrowStyle, setArrowStyle] = useState({})
        const [arrowClass, setArrowClass] = useState('custom-arrow')

        useEffect(() => {
            if (onMount) onMount(popoverRef.current)
            return () => {
                if (onMount) onMount(null)
            }
        }, [onMount])

        useEffect(() => {
            if (!targetRef.current || !popoverRef.current) return

            const rect = targetRef.current.getBoundingClientRect() // toạ độ anchor
            const popoverRect = popoverRef.current.getBoundingClientRect() // toạ độ popover (khung)

            // 1. Tính toạ độ của popover
            let popoverLeft = 0
            let popoverTop = 0

            // Tâm anchor
            const anchorCenterX = rect.left + rect.width / 2 - 10
            const anchorCenterY = rect.top + rect.height / 2

            const arrowSize = 10 // Độ dài mũi tên
            switch (placement) {
                case 'top':
                    popoverLeft = anchorCenterX - popoverRect.width / 2 + popoverOffsetX
                    popoverTop = rect.top - popoverRect.height - arrowSize + popoverOffsetY
                    setArrowClass('custom-arrow custom-arrow-top')
                    break

                case 'bottom':
                    popoverLeft = anchorCenterX - popoverRect.width / 2 + popoverOffsetX
                    popoverTop = rect.bottom + arrowSize + popoverOffsetY
                    setArrowClass('custom-arrow custom-arrow-bottom')
                    break

                case 'left':
                    popoverLeft = rect.left - popoverRect.width - arrowSize + popoverOffsetX
                    popoverTop = anchorCenterY - popoverRect.height / 2 + popoverOffsetY
                    setArrowClass('custom-arrow custom-arrow-left')
                    break

                case 'right':
                    popoverLeft = rect.right + arrowSize + popoverOffsetX
                    popoverTop = anchorCenterY - popoverRect.height / 2 + popoverOffsetY
                    setArrowClass('custom-arrow custom-arrow-right')
                    break

                default:
                    // fallback = top
                    popoverLeft = anchorCenterX - popoverRect.width / 2 + popoverOffsetX
                    popoverTop = rect.top - popoverRect.height - arrowSize + popoverOffsetY
                    setArrowClass('custom-arrow custom-arrow-top')
                    break
            }

            // Viewport boundary check — prevent popover from overflowing right edge
            const viewportWidth = window.innerWidth
            if (popoverLeft + popoverRect.width > viewportWidth - 8) {
                popoverLeft = viewportWidth - popoverRect.width - 8
            }
            // Prevent overflowing left edge
            if (popoverLeft < 8) {
                popoverLeft = 8
            }

            // Cập nhật style cho popover
            const newStyle = {
                left: popoverLeft,
                top: popoverTop,
            }

            // 2. Tính toạ độ của mũi tên so với popover
            //  -- Tức là anchorCenterX (hoặc anchorCenterY) - popoverLeft = khoảng cách
            //  từ cạnh trái (hoặc trên) của popover tới tâm anchor.
            let newArrowStyle = {}
            switch (placement) {
                case 'top':
                case 'bottom': {
                    // arrowX = anchorCenterX - popoverLeft
                    // Rồi thêm transform: translateX(-50%) để mũi tên căn giữa
                    const arrowX = anchorCenterX - popoverLeft
                    newArrowStyle = {
                        left: `${arrowX}px`,
                        transform: 'translateX(-50%)',
                    }
                    if (placement === 'top') {
                        newArrowStyle.bottom = `-${arrowSize}px`
                    } else {
                        newArrowStyle.top = `-${arrowSize}px`
                    }
                    break
                }
                case 'left':
                case 'right': {
                    const arrowY = anchorCenterY - popoverTop
                    newArrowStyle = {
                        top: `${arrowY}px`,
                        transform: 'translateY(-50%)',
                    }
                    if (placement === 'left') {
                        newArrowStyle.right = `-${arrowSize}px`
                    } else {
                        newArrowStyle.left = `-${arrowSize}px`
                    }
                    break
                }
                default:
                    break
            }

            setStyle(newStyle)
            setArrowStyle(newArrowStyle)
        }, [targetRef, placement, popoverOffsetX, popoverOffsetY])

        return (
            <div ref={popoverRef} className="custom-popover popover-content shadow-2" style={style}>
                {children}
                <div className={arrowClass} style={{ ...arrowStyle, '--border-color': borderColor }} />
            </div>
        )
    }
)

export default withPopover

